import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Lock,
  Plus,
  Trash2,
  X,
  RefreshCw
} from 'lucide-react';
import './AdminUsers.css';

const ROLE_LABELS = {
  administrator: { label: 'Администратор', color: '#7c3aed', bg: '#ede9fe' },
  editor:        { label: 'Редактор',       color: '#0369a1', bg: '#e0f2fe' },
  tech_admin:    { label: 'Тех Администратор', color: '#065f46', bg: '#d1fae5' },
  admin:         { label: 'Администратор',  color: '#7c3aed', bg: '#ede9fe' },
};

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', role: 'administrator' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/panel-users');
      if (response.data.success) {
        setUsers(response.data.users || []);
      }
    } catch (err) {
      console.error('Ошибка загрузки администраторов:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setForm({ username: '', password: '', role: 'administrator' });
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setError('');
  };

  const handleCreate = async () => {
    if (!form.username.trim() || !form.password) {
      setError('Заполните все поля');
      return;
    }
    if (form.password.length < 4) {
      setError('Пароль минимум 4 символа');
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post('/api/admin/panel-users', form);
      if (response.data.success) {
        closeModal();
        loadUsers();
      } else {
        setError(response.data.error || 'Ошибка создания');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сервера');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить пользователя?')) return;
    try {
      const response = await api.delete(`/api/admin/panel-users/${id}`);
      if (response.data.success) {
        loadUsers();
      }
    } catch (err) {
      console.error('Ошибка удаления:', err);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('ru-RU');
  };

  return (
    <div className="admin-users-page fade-in">
      <div className="page-header">
        <h1 className="page-title">Администраторы панели</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={loadUsers}>
            <RefreshCw size={18} />
            Обновить
          </button>
          <button className="btn btn-primary" onClick={openModal}>
            <Plus size={18} />
            Добавить пользователя
          </button>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <span>Загрузка...</span>
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <Lock size={48} className="empty-state-icon" />
            <h3 className="empty-state-title">Нет пользователей</h3>
            <p className="empty-state-text">Добавьте первого администратора панели</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Логин</th>
                  <th>Роль</th>
                  <th>Создан</th>
                  <th style={{ textAlign: 'right' }}>Действия</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => {
                  const roleInfo = ROLE_LABELS[user.role] || { label: user.role, color: '#666', bg: '#f0f0f0' };
                  return (
                    <tr key={user.id}>
                      <td className="text-muted">{user.id}</td>
                      <td style={{ fontWeight: 600 }}>{user.username}</td>
                      <td>
                        <span className="role-badge" style={{ color: roleInfo.color, background: roleInfo.bg }}>
                          {roleInfo.label}
                        </span>
                      </td>
                      <td className="text-muted">{formatDate(user.created_at)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(user.id)}
                        >
                          <Trash2 size={14} />
                          Удалить
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Добавить пользователя</h2>
              <button className="modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Логин</label>
                <input
                  type="text"
                  className="input"
                  placeholder="Введите логин"
                  value={form.username}
                  onChange={e => setForm({ ...form, username: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Пароль</label>
                <input
                  type="password"
                  className="input"
                  placeholder="Введите пароль"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Роль</label>
                <select
                  className="input"
                  value={form.role}
                  onChange={e => setForm({ ...form, role: e.target.value })}
                >
                  <option value="administrator">Администратор</option>
                  <option value="editor">Редактор</option>
                  <option value="tech_admin">Тех Администратор</option>
                </select>
              </div>

              {error && <p className="form-error">{error}</p>}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Отмена</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={submitting}>
                {submitting ? 'Создание...' : 'Создать'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
